/**
 * V2 Product Hero — media gallery (Swiper) and variant boxes.
 * Figma: Section Hero Product (339:2271 / 339:2340 / 339:2409)
 * Variant boxes: Bundle variants (1071:11452)
 * Mobile/tablet/desktop: swipe; desktop also shows prev/next arrows.
 */
(function () {
  const SECTION_SELECTOR = '[data-v2-product-hero]';
  const DESKTOP_MQ = '(min-width: 900px)';

  function initProductHero(root) {
    if (!root || root.dataset.v2ProductHeroReady === 'true') return;
    if (typeof Swiper === 'undefined') return;

    const swiperEl = root.querySelector('[data-v2-product-hero-swiper]');
    if (!swiperEl) return;

    const counterEl = root.querySelector('[data-v2-product-hero-counter]');
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    const slideCount = slides.length;
    if (slideCount === 0) return;

    root.dataset.v2ProductHeroReady = 'true';

    const prevEl = root.querySelector('[data-v2-product-hero-prev]');
    const nextEl = root.querySelector('[data-v2-product-hero-next]');
    const hasNav = Boolean(prevEl && nextEl);

    function updateCounter(index) {
      if (!counterEl) return;
      const current = counterEl.querySelector('[data-v2-product-hero-counter-current]');
      if (current) current.textContent = String(index + 1);
    }

    /** Resolve a CSS custom property to a px number (Swiper spaceBetween cannot take var()). */
    function readSpacingPx(property, fallback) {
      const raw = getComputedStyle(root).getPropertyValue(property).trim();
      const value = parseFloat(raw);
      if (Number.isFinite(value)) return value;
      const fromRoot = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(property).trim()
      );
      return Number.isFinite(fromRoot) ? fromRoot : fallback;
    }

    // Mobile spacing/2 (8px); tablet+ spacing/3 (12px)
    const gapMobile = readSpacingPx('--v2-spacing-2', 8);
    const gapTabletUp = readSpacingPx('--v2-spacing-3', 12);

    const options = {
      slidesPerView: 'auto',
      spaceBetween: gapMobile,
      speed: 400,
      watchOverflow: true,
      autoHeight: false,
      loop: false,
      allowTouchMove: slideCount > 1,
      simulateTouch: slideCount > 1,
      grabCursor: slideCount > 1,
      threshold: 5,
      breakpoints: {
        600: {
          spaceBetween: gapTabletUp,
        },
        900: {
          spaceBetween: gapTabletUp,
          allowTouchMove: slideCount > 1,
          simulateTouch: slideCount > 1,
          grabCursor: slideCount > 1,
        },
      },
      on: {
        init(swiper) {
          updateCounter(swiper.realIndex);
        },
        slideChange(swiper) {
          updateCounter(swiper.realIndex);
        },
      },
    };

    if (hasNav) {
      options.navigation = {
        prevEl: prevEl,
        nextEl: nextEl,
        disabledClass: 'swiper-button-disabled',
      };
    }

    const swiper = new Swiper(swiperEl, options);

    function syncNavVisibility() {
      if (!hasNav) return;
      const isDesktop = window.matchMedia(DESKTOP_MQ).matches;
      const show = isDesktop && slideCount > 1;
      prevEl.hidden = !show;
      nextEl.hidden = !show;
      prevEl.setAttribute('aria-hidden', show ? 'false' : 'true');
      nextEl.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    syncNavVisibility();
    window.matchMedia(DESKTOP_MQ).addEventListener('change', syncNavVisibility);

    root._v2ProductHeroSwiper = swiper;
  }

  function initVariantPicker(root) {
    if (!root || root.dataset.v2ProductHeroVariantsReady === 'true') return;

    const group = root.querySelector('[data-v2-product-hero-variants]');
    if (!group) return;

    root.dataset.v2ProductHeroVariantsReady = 'true';

    const cards = Array.prototype.slice.call(group.querySelectorAll('[data-v2-variant-card]'));
    const input = root.querySelector('[data-v2-product-hero-variant-id]');
    const priceEl = root.querySelector('[data-v2-product-hero-price]');
    const parent = root.parentElement;
    const stickyPrice = parent
      ? parent.querySelector('[data-v2-sticky-cta] [data-v2-sticky-cta-price]')
      : null;

    function selectCard(card, shouldFocus) {
      cards.forEach(function (item) {
        const on = item === card;
        item.classList.toggle('is-selected', on);
        const control = item.querySelector('[data-v2-variant-control]');
        if (!control) return;
        control.setAttribute('aria-checked', on ? 'true' : 'false');
        control.tabIndex = on ? 0 : -1;
        if (on && shouldFocus) control.focus();
      });

      const control = card.querySelector('[data-v2-variant-control]');
      const variantId = control ? control.getAttribute('data-v2-variant-id') : '';
      const formatted = control ? control.getAttribute('data-v2-variant-price') : '';

      if (input && variantId) input.value = variantId;
      if (priceEl && formatted) priceEl.textContent = formatted;
      if (stickyPrice && formatted) stickyPrice.textContent = formatted;

      if (root.hasAttribute('data-v2-upsell-trigger') && variantId) {
        document.querySelectorAll('[data-v2-upsell-modal]').forEach(function (modal) {
          modal.setAttribute('data-v2-upsell-main-variant', variantId);
        });
      }
    }

    group.addEventListener('click', function (event) {
      const link = event.target.closest('[data-v2-variant-link]');
      if (link && group.contains(link)) return;

      const card = event.target.closest('[data-v2-variant-card]');
      if (!card || !group.contains(card)) return;
      selectCard(card, false);
    });

    group.addEventListener('keydown', function (event) {
      if (event.target.closest('[data-v2-variant-link]')) return;

      const control = event.target.closest('[data-v2-variant-control]');
      if (!control || !group.contains(control)) return;

      const card = control.closest('[data-v2-variant-card]');
      const index = cards.indexOf(card);
      if (index < 0) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        selectCard(cards[(index + 1) % cards.length], true);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        selectCard(cards[(index - 1 + cards.length) % cards.length], true);
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        selectCard(card, false);
      }
    });
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(function (root) {
      initProductHero(root);
      initVariantPicker(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR) ||
        (event.target.matches && event.target.matches(SECTION_SELECTOR) ? event.target : null)
      : null;
    if (root) {
      delete root.dataset.v2ProductHeroReady;
      delete root.dataset.v2ProductHeroVariantsReady;
      initProductHero(root);
      initVariantPicker(root);
    }
  });

  // Section script is in <main>; Swiper is deferred later in theme.liquid — retry until available
  if (typeof Swiper === 'undefined') {
    const retry = window.setInterval(function () {
      if (typeof Swiper !== 'undefined') {
        window.clearInterval(retry);
        initAll();
      }
    }, 50);
    window.setTimeout(function () {
      window.clearInterval(retry);
    }, 5000);
  }
})();
